/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import truncate from 'lodash/truncate';
import { formatDistance } from 'date-fns/formatDistance';
import { useNavigate } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

// UI lib components
import {
  Menu,
  ActionIcon,
  Group,
  Stack,
  Text,
  Box,
  Button,
  Indicator,
  ThemeIcon,
  useMantineTheme,
  ScrollArea,
  Image,
  lighten,
  rem,
} from '@mantine/core';

// UI local components
import BuildingIcon from '@ui-kit/icons/BuildingIcon';

// Assets
import {
  IconBell as IconNotification,
  IconEyeOff,
  IconPointFilled,
  IconFilePlus,
  IconMessageQuestion as IconSupport,
  IconCursorText as IconRename,
  IconListNumbers as IconReindex,
  IconMessageReply as IconCreateFollowUp,
} from '@tabler/icons-react';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@app/features/Dialog/store/dialogStore';

// Local types & API
import {
  NotificationFragment,
  NotificationsSubscription,
  NotificationTopic,
  Notification,
  useClearNotificationsMutation,
  useGetMyNotificationsLazyQuery,
  useMarkNotificationsAsReadMutation,
} from '@app/types/blackroom-api';
import GET_MY_NOTIFICATIONS from '@features/Notification/api/getMyNotifications';
import NOTIFICATIONS_SUBSCRIPTION from '@features/Notification/api/notificationsSubscription';

// Styles
import { vars } from '@app/shared/themes/vars';
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import { FONT_WEIGHT } from '@ui-kit/fonts';
import { palette } from '@ui-kit/colors';

// Constants
const PAGE_SIZE = 5;

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function NotificationsMenu() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const dialogStore = useReactiveVar(dialogStoreVar);

  const [page, setPage] = useState<number>(0);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] =
    useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const workspace = useWorkspace();
  const workspaceId = workspace.id;

  const [getMyNotifications, { subscribeToMore }] =
    useGetMyNotificationsLazyQuery();

  const [markNotificationsAsRead] = useMarkNotificationsAsReadMutation();
  const [clearNotifications] = useClearNotificationsMutation();

  const { t } = useTranslation();

  const isNotificationsEmpty = allNotifications.length === 0;

  const mappingTitleTranslations = useMemo(
    () => ({
      [NotificationTopic.ResourcesRedacted]: t(
        'Notification:Topics.resourcesRedacted.title',
      ),
      [NotificationTopic.ResourceAdded]: t(
        'Notification:Topics.resourceAdded.title',
      ),
      [NotificationTopic.ResourceRename]: t(
        'Notification:Topics.resourceRename.title',
      ),
      [NotificationTopic.WorkspaceAdminAdded]: t(
        'Notification:Topics.workspaceAdminAdded.title',
      ),
      [NotificationTopic.WorkspaceReindexDone]: t(
        'Notification:Topics.workspaceReindexDone.title',
      ),
      [NotificationTopic.SupportQuestion]: t(
        'Notification:Topics.supportQuestion.title',
      ),
      [NotificationTopic.SupportAssigned]: t(
        'Notification:Topics.supportAssigned.title',
      ),
      [NotificationTopic.SupportAnswer]: t(
        'Notification:Topics.supportAnswer.title',
      ),
      [NotificationTopic.SupportResolved]: t(
        'Notification:Topics.supportResolved.title',
      ),
      [NotificationTopic.SupportDeleted]: t(
        'Notification:Topics.supportDeleted.title',
      ),
      [NotificationTopic.SupportFollowUpAnswer]: t(
        'Notification:Topics.supportFollowUpAnswer.title',
      ),
    }),
    [t],
  );

  const getContentTranslations = useCallback(
    (notification: NotificationFragment) => ({
      [NotificationTopic.ResourcesRedacted]:
        notification.targets && notification.targets.length > 2
          ? t('Notification:Topics.resourcesRedacted.contentWithCount', {
              count: notification.targets.length,
            })
          : t('Notification:Topics.resourcesRedacted.content', {
              fileNames: notification.targets
                ?.map(
                  (target) =>
                    target.__typename === 'Resource' && `${target.name}`,
                )
                .join(', '),
            }),
      [NotificationTopic.ResourceAdded]: t(
        'Notification:Topics.resourceAdded.content',
        {
          fileName: notification.targets?.map(
            (target) => target.__typename === 'Resource' && `${target.name}`,
          )[0],
        },
      ),
      [NotificationTopic.ResourceRename]: t(
        'Notification:Topics.resourceRename.content',
        {
          fileCount: notification.targets?.length ?? 0,
        },
      ),
      [NotificationTopic.WorkspaceAdminAdded]: t(
        'Notification:Topics.workspaceAdminAdded.content',
        {
          workspaceName: notification.targets?.map(
            (target) => target.__typename === 'Workspace' && `${target.name}`,
          )[0],
        },
      ),
      [NotificationTopic.WorkspaceReindexDone]: t(
        'Notification:Topics.workspaceReindexDone.content',
        {
          workspaceName: notification.targets?.map(
            (target) => target.__typename === 'Workspace' && `${target.name}`,
          )[0],
        },
      ),
      [NotificationTopic.SupportQuestion]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
      [NotificationTopic.SupportAssigned]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
      [NotificationTopic.SupportAnswer]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
      [NotificationTopic.SupportResolved]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
      [NotificationTopic.SupportDeleted]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
      [NotificationTopic.SupportFollowUpAnswer]: truncate(
        notification.targets?.map(
          (target) => target.__typename === 'Question' && target.title,
        )[0] || '',
        { length: 166 },
      ),
    }),
    [t],
  );

  const mappingIcons = useMemo(
    () => ({
      [NotificationTopic.ResourcesRedacted]: (
        <ThemeIcon
          color="blackroomGreen"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconEyeOff />
        </ThemeIcon>
      ),
      [NotificationTopic.ResourceAdded]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconFilePlus />
        </ThemeIcon>
      ),
      [NotificationTopic.ResourceRename]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconRename />
        </ThemeIcon>
      ),
      [NotificationTopic.WorkspaceAdminAdded]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <BuildingIcon />
        </ThemeIcon>
      ),
      [NotificationTopic.WorkspaceReindexDone]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconReindex />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportQuestion]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconSupport />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportAssigned]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconSupport />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportAnswer]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconSupport />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportResolved]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconSupport />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportDeleted]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconSupport />
        </ThemeIcon>
      ),
      [NotificationTopic.SupportFollowUpAnswer]: (
        <ThemeIcon
          color="blackroomBlue"
          variant="light"
          size={ICON_SIZE_MAP.large}
          p={4}
          radius="xs"
        >
          <IconCreateFollowUp />
        </ThemeIcon>
      ),
    }),
    [],
  );

  const mappingActionsOnClick = useCallback(
    (notification: NotificationFragment) => ({
      [NotificationTopic.ResourcesRedacted]: async () => {
        markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        window.open(
          `/workspace/${workspace.id}/file/${
            notification.targets?.map(
              (target) => target.__typename === 'Resource' && target.id,
            )[0]
          }`,
        );
      },
      [NotificationTopic.ResourceAdded]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        window.open(
          `/workspace/${workspace.id}/file/${
            notification.targets?.map(
              (target) => target.__typename === 'Resource' && target.id,
            )[0]
          }`,
        );
      },
      [NotificationTopic.ResourceRename]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
      },
      [NotificationTopic.WorkspaceAdminAdded]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${notification.targets?.filter((target) => target.__typename === 'Workspace')[0].id}`,
        );
      },
      [NotificationTopic.WorkspaceReindexDone]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(`/workspace/${notification.workspaceId}`);
      },
      [NotificationTopic.SupportQuestion]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${workspace.id}/support?questionId=${notification.targets?.filter((target) => target.__typename === 'Question')[0].id}`,
        );
      },
      [NotificationTopic.SupportAssigned]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${workspace.id}/support?questionId=${notification.targets?.filter((target) => target.__typename === 'Question')[0].id}`,
        );
      },
      [NotificationTopic.SupportAnswer]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${workspace.id}/support?questionId=${notification.targets?.filter((target) => target.__typename === 'Question')[0].id}`,
        );
      },
      [NotificationTopic.SupportResolved]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${workspace.id}/support?questionId=${notification.targets?.filter((target) => target.__typename === 'Question')[0].id}`,
        );
      },
      [NotificationTopic.SupportDeleted]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
      },
      [NotificationTopic.SupportFollowUpAnswer]: async () => {
        await markNotificationsAsRead({
          variables: { notificationIds: [notification.id], workspaceId },
          refetchQueries: [
            {
              query: GET_MY_NOTIFICATIONS,
              variables: { filter: { workspaceId: workspace.id } },
            },
          ],
        });
        await fetchNotifications();
        navigate(
          `/workspace/${workspace.id}/support?questionId=${notification.targets?.filter((target) => target.__typename === 'Question')[0].id}`,
        );
      },
    }),
    [workspace.id, dialogStore?.dialogIdentifier],
  );

  async function fetchNotifications() {
    setLoading(true);
    const { data: notificationData } = await getMyNotifications({
      variables: {
        filter: { workspaceId: workspace.id },
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      },
      fetchPolicy: 'network-only',
    });
    const newNotifications =
      (notificationData?.myNotifications.nodes as Notification[] | undefined) ??
      [];

    const allFetchedNotifications = [...allNotifications, ...newNotifications];
    if (
      notificationData?.myNotifications.pageInfo?.totalCount &&
      allFetchedNotifications.length >=
        notificationData?.myNotifications.pageInfo?.totalCount
    ) {
      setHasNextPage(false);
    }
    setAllNotifications(allFetchedNotifications);
    setUnreadNotificationsCount(
      notificationData?.myNotifications.unreadCount ?? 0,
    );
    setTotalCount(notificationData?.myNotifications.pageInfo?.totalCount ?? 0);
    setPage(page + 1);
    setLoading(false);
  }

  useEffect(() => {
    subscribeToMore({
      document: NOTIFICATIONS_SUBSCRIPTION,
      variables: { workspaceId: workspace.id },
      updateQuery: (
        prev,
        {
          subscriptionData,
        }: { subscriptionData: { data: NotificationsSubscription } },
      ) => {
        if (!subscriptionData.data) return prev;
        const newNotification = subscriptionData.data
          .notifications as Notification;
        fetchNotifications();

        return {
          myNotifications: {
            ...prev.myNotifications,
            nodes: [newNotification, ...(prev.myNotifications.nodes || [])],
            pageInfo: {
              ...prev.myNotifications.pageInfo,
              totalCount: (prev.myNotifications.pageInfo?.totalCount || 0) + 1,
            },
          },
        };
      },
    });
    setAllNotifications([]);
    setPage(0);
    setUnreadNotificationsCount(0);
    setHasNextPage(false);
    setLoading(false);
    (async function asyncWrapper() {
      await fetchNotifications();
    })();
  }, [workspace.id]);

  // fetch  initial product list
  useEffect(() => {
    (async function asyncWrapper() {
      if (page === 0) {
        await fetchNotifications();
      }
    })();
  }, [page]);

  const [listItemRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: fetchNotifications,
  });

  return (
    <Box>
      <Menu
        shadow="md"
        position="bottom-end"
        width="406px"
        closeOnItemClick={false}
      >
        <Menu.Target>
          <Indicator
            styles={{
              indicator: {
                borderRadius: vars.radius.lg,
                padding: `${rem(7)} ${rem(4)} ${rem(7)} ${rem(4)}`,
              },
            }}
            position="top-end"
            color="blackroomRed"
            size={ICON_SIZE_MAP.tiny}
            label={unreadNotificationsCount}
            disabled={unreadNotificationsCount < 1}
            inline
            role="button"
          >
            <ActionIcon
              radius="xl"
              variant="transparent"
              aria-label={t('Notification:title')}
            >
              <ThemeIcon size="md" color="blackroomBlack" variant="transparent">
                <IconNotification size={ICON_SIZE_MAP.small} stroke={1} />
              </ThemeIcon>
            </ActionIcon>
          </Indicator>
        </Menu.Target>
        <Menu.Dropdown pt="md" px={0} pb={0} ref={rootRef}>
          <Box px={30} pb={6}>
            <Group style={{ gap: 8 }}>
              <Text size="xl" c="blackroomBlack" fw={FONT_WEIGHT.bold}>
                {t('Notification:title')}
              </Text>
              {unreadNotificationsCount > 0 && (
                <Box
                  bg="blackroomRed"
                  w={30}
                  h={30}
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: theme.radius.xs,
                  }}
                >
                  <Text color="blackroomWhite" fw={FONT_WEIGHT.bolder}>
                    {unreadNotificationsCount}
                  </Text>
                </Box>
              )}
            </Group>

            {!isNotificationsEmpty && (
              <Group my={16}>
                <Button
                  variant="outline"
                  style={{ flex: 1 }}
                  onClick={async () => {
                    await markNotificationsAsRead({
                      variables: {
                        workspaceId,
                      },
                    });
                    setAllNotifications([]);
                    setLoading(true);
                    setPage(0);
                    setHasNextPage(false);
                    setUnreadNotificationsCount(0);
                    await fetchNotifications();
                  }}
                >
                  <Text size="xs">
                    {t('Notification:Actions.markAllAsRead')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  style={{ flex: 1 }}
                  onClick={async () => {
                    clearNotifications({
                      variables: {
                        workspaceId,
                      },
                    });
                    await fetchNotifications();
                    setAllNotifications([]);
                    setLoading(false);
                    setPage(0);
                    setHasNextPage(false);
                    setUnreadNotificationsCount(0);
                  }}
                >
                  <Text size="xs">{t('Notification:Actions.clearAll')}</Text>
                </Button>
              </Group>
            )}
          </Box>

          <Menu.Divider m={0} />

          {isNotificationsEmpty && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem 0',
              }}
            >
              <Stack>
                <Image
                  alt="Notifications"
                  src="/images/icons/emptyNotifications.svg"
                  width={150}
                  height={150}
                  loading="eager"
                />
                <Text>{t('Notification:emptyNotifications')}</Text>
              </Stack>
            </Box>
          )}

          <ScrollArea.Autosize mah={300}>
            {allNotifications.map((notification, index) => (
              <Fragment key={notification.id}>
                <Menu.Item
                  ref={listItemRef}
                  style={{ borderRadius: 0 }}
                  bg={
                    notification.readAt === null
                      ? lighten(palette.blackroomBlue, 0.8)
                      : ''
                  }
                  pl={notification.readAt !== null ? 30 : 4}
                  pr={30}
                  closeMenuOnClick={[NotificationTopic.ResourceRename].includes(
                    notification.topic,
                  )}
                  leftSection={
                    <>
                      {notification.readAt === null && (
                        <ThemeIcon
                          variant="outline"
                          style={{ border: 0 }}
                          color="blackroomRed"
                        >
                          <IconPointFilled />
                        </ThemeIcon>
                      )}
                      {mappingIcons[notification.topic]}
                    </>
                  }
                  rightSection={
                    <Text size="xs" c={palette.blackroomLightGray2}>
                      {formatDistance(
                        new Date(notification.createdAt),
                        new Date(),
                      )}
                    </Text>
                  }
                  onClick={
                    mappingActionsOnClick(notification)[notification.topic]
                  }
                >
                  <Group pr={20}>
                    <Stack gap={0}>
                      <Text size="md" c="blackroomBlack" fw={FONT_WEIGHT.bold}>
                        {mappingTitleTranslations[notification.topic]}
                      </Text>
                      <Text
                        size="xs"
                        c={palette.blackroomLightGray2}
                        fw={FONT_WEIGHT.lighter}
                        dangerouslySetInnerHTML={{
                          // not dangerous since we are translating with react-i18n which sanitizes user input
                          __html:
                            getContentTranslations(notification)[
                              notification.topic
                            ],
                        }}
                      />
                    </Stack>
                  </Group>
                </Menu.Item>

                {index < (totalCount || 0) - 1 && <Menu.Divider m={0} />}
              </Fragment>
            ))}
          </ScrollArea.Autosize>
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}

export default NotificationsMenu;
